import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate L6-10 dello Sciamano (manuale V1, rr. 5681-5766).
 *
 * 14 mosse di tipo `advanced_6_10`. Pattern stress-testati:
 *
 * - **Catene di prerequisiti L2-5 → L6-10** (3 catene):
 *   - *Che Tu Sia...* (L2-5) → *Nel Bene o nel Male* (L6-10)
 *   - *Bambola Vudù* (L2-5) → *Magia Simpatetica* (L6-10)
 *   - *Onorevoli Padri* (auto) → *Concilio Ancestrale* (L6-10)
 *
 * - **FI-8 stub description-only** (D3): *Apocalisse Zombi* (orda di zombi
 *   temporanei) e *Uno con lo Spirito* (possessione che concede mosse runtime
 *   — analogo concettuale ai gregari). Refactor unificato Ranghi 1-3 nella PR
 *   FI-8 post-12 classi.
 *
 * - **Mossa multiclasse** (FI-17): *Figlio degli Spiriti* sceglie da Barbaro o
 *   Druido (extension all'archetipo Elfo: anche Mago). Description-only.
 *
 * - **Cross-class ref testuale**: *Squarciare il Velo* cita *Che Tu Sia...*
 *   come prereq alternativo nel testo (in realtà la mossa accetta «una
 *   condizione della lista di *Che Tu Sia...*»: la condizione è citata, non la
 *   presa preventiva della mossa). Description-only.
 */

const figlioDegliSpiriti: Move = {
  id: 'sciamano.figlio-degli-spiriti',
  name: 'Figlio degli Spiriti',
  type: 'advanced_6_10',
  classId: 'sciamano',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    "Scegli una mossa non multiclasse dalla lista del Barbaro o del Druido. Se hai l'archetipo Elfo, puoi pescare anche dal Mago.",
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-17 (D7): pool Barbaro/Druido + Elfo→Mago (simmetrico a *Amico degli Spiriti*).
  multiclass: {
    picks: { kind: 'fixed-classes', classes: ['barbaro', 'druido'] },
    extendsByArchetype: [{ archetypeId: 'elfo', extraClasses: ['mago'] }],
  },
};

// FI-8 stub: l'orda di zombi temporanei è description-only (gregari evocati,
// refactor unificato nella PR FI-8 post-12 classi).
const apocalisseZombi: Move = {
  id: 'sciamano.apocalisse-zombi',
  name: 'Apocalisse Zombi',
  type: 'advanced_6_10',
  classId: 'sciamano',
  trigger:
    'Quando invochi degli spiriti voraci affinché posseggano dei cadaveri che si trovano nelle vicinanze',
  description:
    "Spendi 3 prese di *Aiuto dall'Altro Mondo*, spiega cosa vuoi che facciano, e tira+**CAR** (applicando i modificatori al tiro che hai per *Aiuto dall'Altro Mondo*). Con 7+, l'orda esegue il compito assegnato al meglio delle sue possibilità, seminando distruzione e terrore. Con un 10+, scegli 2. Con 7-9, tu scegli 1 e il GM sceglie 1:\n\n- l'armata delle tenebre non causa più distruzione del dovuto mentre esegue il suo compito;\n- l'orda di zombi non causa più terrore del dovuto mentre svolge il compito assegnato;\n- il tuo legame con il mondo degli spiriti non viene sovraccaricato.\n\nIn ogni caso, la prossima volta che ti troverai faccia a faccia con la Morte, dovrai dare delle spiegazioni per il disturbo arrecato.",
  roll: {
    stat: 'carisma',
    on10plus: { text: "L'orda esegue il compito + scegli 2 condizioni dalla lista." },
    on7to9: { text: "L'orda esegue il compito + tu scegli 1 e il GM sceglie 1." },
  },
  tags: [],
  prerequisites: ['sciamano.aiuto-dall-altro-mondo'],
  replaces: [],
};

const spiritiIngrati: Move = {
  id: 'sciamano.spiriti-ingrati',
  name: 'Spiriti Ingrati',
  type: 'advanced_6_10',
  classId: 'sciamano',
  trigger: 'Quando versi il sangue di una creatura',
  description:
    "Puoi spendere una presa di *Aiuto dall'Altro Mondo* per forzarla ad attaccare un bersaglio a tua scelta.",
  tags: [],
  prerequisites: ['sciamano.aiuto-dall-altro-mondo'],
  replaces: [],
};

const spiritiVendicativi: Move = {
  id: 'sciamano.spiriti-vendicativi',
  name: 'Spiriti Vendicativi',
  type: 'advanced_6_10',
  classId: 'sciamano',
  trigger: 'Quando subisci danno da un nemico',
  description:
    "Puoi infliggere all'avversario 1d6 danni con etichetta *di energia spirituale* per ogni presa di *Aiuto dall'Altro Mondo* che decidi di spendere (senza aggiungere altri bonus o applicare ulteriori etichette).",
  tags: [],
  prerequisites: ['sciamano.aiuto-dall-altro-mondo'],
  replaces: [],
};

const potereAntico: Move = {
  id: 'sciamano.potere-antico',
  name: 'Potere Antico',
  type: 'advanced_6_10',
  classId: 'sciamano',
  trigger: 'Quando sei il bersaglio di una magia',
  description:
    "Puoi spendere 1 presa di *Aiuto dall'Altro Mondo* affinché essa non abbia alcun effetto su di te.",
  tags: [],
  prerequisites: ['sciamano.aiuto-dall-altro-mondo'],
  replaces: [],
};

const nelBeneONelMale: Move = {
  id: 'sciamano.nel-bene-o-nel-male',
  name: 'Nel Bene o nel Male',
  type: 'advanced_6_10',
  classId: 'sciamano',
  trigger: 'Quando usi la mossa *Che Tu Sia…*',
  description:
    "Ora sei in grado di invocare sia la *benevolenza* che la *malevolenza* degli spiriti dell'aldilà.",
  tags: [],
  prerequisites: ['sciamano.che-tu-sia'],
  replaces: [],
  // FI-31 (D5): concede la `choice` rimanente di `sciamano.che-tu-sia`
  // (benevolenza/malevolenza, pool=1 residuo).
  grantsMoveChoice: { kind: 'from-choice-move', moveId: 'sciamano.che-tu-sia' },
};

const sguardoDelLupo: Move = {
  id: 'sciamano.sguardo-del-lupo',
  name: 'Sguardo del Lupo',
  type: 'advanced_6_10',
  classId: 'sciamano',
  trigger:
    'Quando fissi negli occhi una creatura senziente e fai scorrere il tuo sangue annunciando la sua futura rovina',
  description:
    'Subisci 2 danni (*ignora armatura*) e tira+**CAR**. Con un 10+, scegli come reagisce il bersaglio: fugge, attacca o resta inchiodato sul posto. Con 7-9, è il GM a decidere la sua reazione. Con 6-, il bersaglio ignora la tua profezia e sei tu a essere scosso dalla sua noncuranza: prendi -1 al prossimo tiro quando agisci contro di lui.',
  roll: {
    stat: 'carisma',
    on10plus: { text: 'Scegli la reazione del bersaglio (fugge / attacca / inchiodato).' },
    on7to9: { text: 'Il GM decide la reazione del bersaglio.' },
    on6minus: { text: 'Il bersaglio ignora la profezia: -1 al prossimo tiro contro di lui.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const concilioAncestrale: Move = {
  id: 'sciamano.concilio-ancestrale',
  name: 'Concilio Ancestrale',
  type: 'advanced_6_10',
  classId: 'sciamano',
  trigger:
    "Quando passi alcuni minuti invocando l'intero concilio dei tuoi antenati per ricevere il loro aiuto",
  description:
    'Tira+**CAR**. Con 10+, scegli in che modo questo si manifesta:\n\n- ti viene svelata una verità nascosta o suggerita la soluzione a un problema che ti affligge. La rivelazione è chiara e veritiera;\n- tre guerrieri ancestrali ti affiancano. Possono dimezzare un danno che hai appena subito (arrotondando per eccesso) o consentirti di infliggere +1d6 danni con un tuo attacco. Puoi essere aiutato da un solo guerriero per volta, e questi tornerà al concilio subito dopo averti assistito;\n- tu e i tuoi alleati che si trovano nei paraggi venite curati di 10 PF;\n- una maledizione che affligge un tuo alleato o il luogo in cui ti trovi viene estirpata. Le maledizioni più potenti (a discrezione del GM) vengono solo rese innocue per qualche tempo.\n\nCon 7-9, gli antenati ti aiutano comunque nel modo che desideri, ma il GM ti dice cosa vogliono in cambio. Con 6-, il concilio non ti concede il suo aiuto, ma pretende comunque qualcosa da te.',
  roll: {
    stat: 'carisma',
    on10plus: { text: 'Scegli 1 dei 4 effetti del concilio.' },
    on7to9: { text: 'Gli antenati ti aiutano + il GM ti dice cosa vogliono in cambio.' },
    on6minus: { text: 'Niente aiuto, ma il concilio pretende qualcosa da te.' },
  },
  tags: [],
  prerequisites: ['sciamano.onorevoli-padri'],
  replaces: [],
};

const viaggioSciamanico: Move = {
  id: 'sciamano.viaggio-sciamanico',
  name: 'Viaggio Sciamanico',
  type: 'advanced_6_10',
  classId: 'sciamano',
  trigger: 'Quando entri in una trance tanto profonda da separare il tuo spirito dal corpo',
  description:
    "Tira+**SAG**. Con 10+, la proiezione avviene con successo. Con 7-9, riesci a lasciare il corpo, ma la connessione con esso è debole e puoi starne fuori solo per pochi minuti. Mentre sei in forma di spirito non puoi interagire con il mondo dei viventi, sei invisibile per coloro che non possono vedere il mondo dell'aldilà e il tuo corpo rimane inerme e indifeso.",
  roll: {
    stat: 'saggezza',
    on10plus: { text: 'Proiezione astrale con successo, durata illimitata.' },
    on7to9: {
      text: 'Proiezione astrale ma connessione debole: solo pochi minuti fuori dal corpo.',
    },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const unoConLoSpirito: Move = {
  id: 'sciamano.uno-con-lo-spirito',
  name: 'Uno con lo Spirito',
  type: 'advanced_6_10',
  classId: 'sciamano',
  trigger:
    "Quando invochi l'aiuto di uno spirito dell'aldilà e gli permetti di possedere il tuo corpo",
  description:
    'Dì quale tipo di spirito stai invocando e tira+**CAR**. Con 10+, ottieni 3 prese e il GM ti accorda due mosse associate a tale spirito. Con un 7-9, prendi 2 e ti viene accordata una sola mossa.\n\nSpendi una presa per fare una delle mosse accordate, che funziona senza bisogno di tirare. La possessione finisce alla prossima alba o quando termini le prese.\n\n**NB architetturale (FI-8 stub, decisione 2026-05-10)**: la possessione concede mosse runtime accordate dal GM — analogo concettuale del sistema gregari (capacità modulari concesse). Refactor unificato nella PR FI-8 post-12 classi.',
  roll: {
    stat: 'carisma',
    on10plus: { text: '3 prese + 2 mosse accordate dal GM.' },
    on7to9: { text: '2 prese + 1 mossa accordata dal GM.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const squarciareIlVelo: Move = {
  id: 'sciamano.squarciare-il-velo',
  name: 'Squarciare il Velo',
  type: 'advanced_6_10',
  classId: 'sciamano',
  trigger: 'Quando offici un rituale per indebolire il velo tra i mondi',
  description:
    "Se accetti una condizione della lista di *Che Tu Sia…*, nomina e descrivi una regione dell'aldilà. Il suo «clima» (venti torridi, echi amplificanti, pioggia guaritrice, a discrezione del GM) permeerà questo mondo nel raggio di alcuni chilometri, fino alla prossima alba.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const magiaSimpatetica: Move = {
  id: 'sciamano.magia-simpatetica',
  name: 'Magia Simpatetica',
  type: 'advanced_6_10',
  classId: 'sciamano',
  trigger:
    'Quando costruisci una bambola con le sembianze di qualcuno che hai incontrato, vi incorpori qualcosa di suo e ci leghi il tuo spirito',
  description:
    'Prendi una Debilitazione a tua scelta. Se la cosa che hai incorporato è un oggetto a lui caro, il tuo bersaglio non può agire direttamente contro di te finché hai la bambola. Se, invece, la cosa che hai inserito è una parte del suo corpo, distruggendo la bambola puoi infliggergli 3d6 danni con etichette *ignora armatura* e *di energia spirituale*. Fino a quando la bambola esiste, non puoi curare normalmente la Debilitazione. Se hai già tutte le Debilitazioni segnate, non puoi usare questa mossa.',
  tags: [],
  prerequisites: ['sciamano.bambola-vudu'],
  replaces: [],
};

const mangiatoreDiPeccati: Move = {
  id: 'sciamano.mangiatore-di-peccati',
  name: 'Mangiatore di Peccati',
  type: 'advanced_6_10',
  classId: 'sciamano',
  trigger: 'Quando tocchi pelle a pelle una creatura morente o morta da meno di un giorno',
  description:
    'Puoi intercedere in suo favore con la Morte. Il Signore dei Neri Cancelli rinuncerà, per il momento, alla sua anima, ma ti chiederà in cambio un favore o un sacrificio appropriato cui potrai adempiere nei tempi che preferisci. Se, però, la prossima volta che esali il tuo *ultimo respiro* non hai ancora soddisfatto il Cupo Mietitore, fallisci automaticamente il tiro. In ogni caso, il tuo bersaglio torna in vita con 1 PF, e con le Debilitazioni, le malattie e qualsiasi altra condizione di cui stava soffrendo prima di morire.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const ingannareLaMorte: Move = {
  id: 'sciamano.ingannare-la-morte',
  name: 'Ingannare la Morte',
  type: 'advanced_6_10',
  classId: 'sciamano',
  trigger: "Quando esali l'*ultimo respiro*",
  description:
    "La Morte ti offre sempre un accordo, anche con un 6-. Se non mantieni la tua parte dell'accordo, rimani comunque in vita, ma la Morte reclamerà immediatamente la tua anima la prossima volta che ti presenterai al suo cospetto.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

export const sciamanoAdvanced6to10Moves: readonly Move[] = [
  figlioDegliSpiriti,
  apocalisseZombi,
  spiritiIngrati,
  spiritiVendicativi,
  potereAntico,
  nelBeneONelMale,
  sguardoDelLupo,
  concilioAncestrale,
  viaggioSciamanico,
  unoConLoSpirito,
  squarciareIlVelo,
  magiaSimpatetica,
  mangiatoreDiPeccati,
  ingannareLaMorte,
] as const;
